import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Download, CheckCircle2, MapPin } from "lucide-react";
import OrderDetailModal from "@/components/OrderDetailModal";

const peso = (n) => `₱${(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

const ReleasingDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/orders");
        if (!cancelled) setOrders(data.filter((o) => ["ready_for_pickup", "shipped"].includes(o.status)));
      } catch { if (!cancelled) toast.error("Failed to load"); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const reload = async () => {
    try {
      const { data } = await api.get("/orders");
      setOrders(data.filter((o) => ["ready_for_pickup", "shipped"].includes(o.status)));
    } catch { toast.error("Failed to reload"); }
  };

  const complete = async (o) => {
    try {
      await api.patch(`/orders/${o.order_id}/status`, { status: "completed" });
      toast.success(`Order ${o.order_id} completed`);
      setDetail(null);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openDetail = async (o) => {
    try {
      const { data } = await api.get(`/orders/${o.order_id}`);
      setDetail(data);
    } catch { toast.error("Failed to load order"); }
  };

  if (loading) return <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>;

  const pickup = orders.filter((o) => o.fulfillment_type === "pickup");
  const delivery = orders.filter((o) => o.fulfillment_type === "delivery");

  const Card = ({ o }) => (
    <div className="pav-card cursor-pointer hover:bg-[#F8FAFC] transition"
      onClick={() => openDetail(o)}
      data-testid={`release-card-${o.order_id}`}>
      <div className="flex justify-between items-start mb-2">
        <div>
          <p className="font-semibold">{o.order_id}</p>
          <p className="text-xs text-neutral-500">{o.customer_name} · {o.customer_phone}</p>
        </div>
        <p className="font-display font-bold text-[#0B1E3F]">{peso(o.total)}</p>
      </div>
      {o.payment_status === "unpaid" && (
        <p className="text-[11px] text-red-600 font-semibold uppercase mb-2">⚠ On Account — Unpaid</p>
      )}
      {o.payment_status === "partial" && (
        <p className="text-[11px] text-orange-600 font-semibold uppercase mb-2">⚠ Partial / DP</p>
      )}
      <div className="text-xs text-neutral-600 space-y-0.5">
        {o.items?.map((it, i) => (
          <p key={i}>{it.service_name || it.category} · {it.width}×{it.height} {it.unit} ×{it.quantity}</p>
        ))}
      </div>
      {o.fulfillment_type === "pickup" ? (
        <p className="text-xs mt-2 text-neutral-500">📍 Pickup: {o.pickup_datetime ? new Date(o.pickup_datetime).toLocaleString() : "anytime"}</p>
      ) : (
        <div className="mt-2 text-xs">
          <p className="text-neutral-600">{o.shipping?.receiver_name} · {o.shipping?.address_line1}, {o.shipping?.city}</p>
          {o.google_map_link && (
            <a href={o.google_map_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
              className="text-[#0B1E3F] hover:underline inline-flex items-center gap-1 mt-1">
              <MapPin className="h-3 w-3" /> Google Maps
            </a>
          )}
        </div>
      )}
      {o.items?.[0]?.file_url && (
        <a href={fileUrl(o.items[0].file_url)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-xs text-[#0B1E3F] hover:underline mt-2">
          <Download className="h-3 w-3" /> Print file
        </a>
      )}
      <button onClick={(e) => { e.stopPropagation(); complete(o); }} data-testid={`release-complete-${o.order_id}`}
        className="mt-3 w-full btn-accent text-xs py-2 inline-flex items-center justify-center gap-1.5">
        <CheckCircle2 className="h-3.5 w-3.5" /> Mark as released / completed
      </button>
    </div>
  );

  return (
    <div className="p-6 lg:p-10 pav-fade-in" data-testid="releasing-dashboard">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Releasing</p>
        <h1 className="font-display text-3xl font-extrabold text-[#0B1E3F] mt-1">Ready for release</h1>
        <p className="text-sm text-neutral-500 mt-1">Click a card to view full details. Hand over to customers and complete the order.</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="font-display font-bold text-lg mb-3">🏪 Store pickup ({pickup.length})</h2>
          <div className="space-y-3">
            {pickup.length === 0 && <p className="text-sm text-neutral-500">None to release.</p>}
            {pickup.map((o) => <Card key={o.order_id} o={o} />)}
          </div>
        </div>
        <div>
          <h2 className="font-display font-bold text-lg mb-3">🚚 Delivery ({delivery.length})</h2>
          <div className="space-y-3">
            {delivery.length === 0 && <p className="text-sm text-neutral-500">None to release.</p>}
            {delivery.map((o) => <Card key={o.order_id} o={o} />)}
          </div>
        </div>
      </div>

      <OrderDetailModal order={detail} onClose={() => setDetail(null)}
        actions={detail && (
          <button onClick={() => complete(detail)} className="btn-accent text-sm inline-flex items-center gap-2" data-testid="modal-complete-button">
            <CheckCircle2 className="h-4 w-4" /> Mark as released / completed
          </button>
        )} />
    </div>
  );
};

export default ReleasingDashboard;
